import { nebulaClient, nebulaClient2 } from "../../lib/nebulaClient";
import { GetPatientsParams, PaginatedPatientsResponse, PatientService } from "./nebula.service";
import { IPatient, Tag } from "./patientsSlice";

export interface PatientDTO {
    isQc: boolean;
    earliestISODate: string;
    lastUploadedOnIso: string;
    dateOfBirth: string;
    gender: string;
    group: string;
    label: string;
    latestISODate: string;
    siteName: string;
    patientId: string;
    patientName: string;
    uuid: string;
    site: string;
    visits: number;
    laterality: "OS" | "OD" | "OU" | undefined;
    isRemoved: boolean;
    tags: {
        uuid: string;
        label: string;
        color: string;
    }[];
    workflowStates: {
        value: string;
        label: string;
        priority?: string;
        order?: number;
    }[];
}

interface TagDto {
    uuid: string;
    label: string;
    color: string;
    createdAt: Date;
    updatedAt: Date;
}

export const patientService: PatientService = {
    getPatients: async ({
        trialId,
        page = 1,
        limit = 20,
        search,
        sortBy,
        sortOrder,
        isQc,
        showRemoved,
    }: GetPatientsParams) => {
        const queryParams = new URLSearchParams();

        queryParams.set("page", String(page));
        queryParams.set("limit", String(limit));

        if (search) queryParams.set("search", search);
        if (sortBy) queryParams.set("sortBy", sortBy);
        if (sortOrder) queryParams.set("sortOrder", sortOrder);
        if (typeof isQc === "boolean") queryParams.set("isQc", String(isQc));
        if (showRemoved) queryParams.set("showRemoved", "true");

        const queryString = queryParams.toString();
        const response = await nebulaClient2.get<PaginatedPatientsResponse>(
            `/trial/${trialId}/patient${queryString ? `?${queryString}` : ""}`
        );

        return response;
    },
    getPatientById: async (patientId: string) => {
        const response = await nebulaClient2.get<PatientDTO>(`/patient/${patientId}`);

        return response;
    },
    editPatient: async (patient: Partial<IPatient>) => {
        const { uuid, ...rest } = patient;
        const response = await nebulaClient2.put<PatientDTO>(`/patient/${patient.uuid}`, rest);

        return response;
    },
    removePatient: async (patientId: string) => {
        const response = await nebulaClient2.delete<void>(`/patient/${patientId}`);

        return response;
    },
    restorePatient: async (patientId: string) => {
        const response = await nebulaClient2.put<void>(`/patient/${patientId}/restore`, {});

        return response;
    },
    getTags: async (trialId: string) => {
        const response = await nebulaClient2.get<TagDto[]>(`/trial/${trialId}/tag`);

        return response;
    },
    removeTagFromPatient: async (patientId: string, tagId: string) => {
        const response = await nebulaClient2.delete<PatientDTO>(`/patient/${patientId}/tag/${tagId}`);

        return response;
    },
    addTagToPatient: async (patientId: string, tag: Tag) => {
        const response = await nebulaClient2.post<PatientDTO>(`/patient/${patientId}/tag/${tag.uuid}`, {});

        return response;
    },
};
